import React, { useState } from 'react';
import { Checkbox, Popup } from 'semantic-ui-react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import toast from 'react-hot-toast';
import ReactGA from 'react-ga4';

import Modal from '~/components/Modal';
import MarketplaceBadge from './MarketplaceBadge';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { toggleMarketplaceInterest } from '~/api/marketplace';
import { logUserActivity, UserActivityTypes } from '~/api/user';

// type Listing = {
//   totalSize?: number;
//   totalStorage?: number;
//   // TODO
// };

type MarketplaceListingProps = {
  listing: any;
  onChange?: () => void; // Optional callback for when interest is toggled
};

const PROJECT_TYPE_DISPLAY: Record<string, string> = {
  CI: 'C&I',
  COMMUNITY_SOLAR: 'Community',
  UTILITY: 'Utility',
};

const MarketplaceListing: React.FC<MarketplaceListingProps> = ({ listing, onChange }) => {

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { permissions } = usePermissions();

  const bidDeadlineDisplay = (() => {
    if (!listing?.bidDeadline) return <i>Open</i>;
    // const deadline = new Date(listing.bidDeadline);
    const deadline = utcToZonedTime(listing.bidDeadline, 'UTC');

    const today = new Date();
    if (deadline < today) return <i>Closed</i>;
    return format(deadline, 'M/d/yyyy');
  })();

  const stateDisplay = (() => {
    return listing?.state.length === 1 ?
      listing?.state :
      <Popup
        content={Array.isArray(listing?.state) ? listing.state.join(', ') : listing?.state}
        trigger={
          <span>
            {Array.isArray(listing?.state) ? listing.state.length : 0} states
          </span>
        }
        position="top right"
        hoverable
      />;
  })();

  const deploymentDisplay = (() => {
    if (!listing?.deploymentType) return null;
    return listing.deploymentType.length === 1 ?
      PROJECT_TYPE_DISPLAY[listing.deploymentType[0]] :
      <Popup
        content={Array.isArray(listing?.deploymentType) ? listing.deploymentType.map((type: string) => PROJECT_TYPE_DISPLAY[type] || type).join(', ') : listing?.deploymentType}
        trigger={
          <span>
            {Array.isArray(listing?.deploymentType) ? listing.deploymentType.length : 0} types
          </span>
        }
        position="top right"
        hoverable
      />;
  })();

  const interestBadgeDescription =
    listing.isInterested
      ? ((new Date(listing.bidDeadline) > new Date() || !listing.bidDeadline)
        ? 'You have expressed interest in this deal to Conductor. If you are a good match, we will invite you to bid.'
        : 'You have expressed interest in this deal to Conductor.')
      : ((new Date(listing.bidDeadline) > new Date() || !listing.bidDeadline)
        ? 'Click here to express interest in this deal to Conductor. If you are a good match, we will invite you to bid.'
        : 'Click here to express interest in this deal so Conductor can find good matches for you in the future.');

  const dealMaturityClassName = listing?.dealMaturity ? listing.dealMaturity.toLowerCase().replace(/\s+/g, '-') : '';

  const toggleInterest = () => {
    toggleMarketplaceInterest(listing.id, { isInterested: !listing.isInterested })
      .then(() => {
        if (!listing.isInterested) {
          const projectId = listing.projectId ?? null;
          const portfolioId = listing.portfolioId ?? null;
          if (projectId || portfolioId) {
            logUserActivity({
              activityType: UserActivityTypes.LISTING_INTEREST,
              ...(projectId ? { projectId } : { portfolioId }),
              metadata: { marketplaceListingId: listing.id },
            });
          }
        }
        ReactGA.event({
          category: 'Marketplace',
          action: listing.isInterested ? 'Retract interest' : 'Express interest',
          label: listing?.totalSize ? `${listing.totalSize} MWdc` : undefined
        });
        if (onChange) {
          onChange();
        }
      })
      .catch((error) => {
        toast.error(
          listing.isInterested ?
            'Failed to remove interest in this deal.' :
            'Failed to express interest in this deal.'
        );
      });
  };

  const handleOpenModal = () => {
    setModalIsOpen(true);
    ReactGA.event({
      category: 'Marketplace',
      action: 'View listing',
      label: listing?.totalSize ? `${listing.totalSize} MWdc` : undefined
    });
  };

  return (
    <div className="marketplace-listing">
      <div className="listing-card" onClick={() => handleOpenModal()}>
        <div className="listing-header">
          <div className="listing-badges">
            {listing?.badges?.map((badge: any, idx: number) => (
              <MarketplaceBadge key={idx} badge={badge} />
            ))}
            {listing?.totalStorage > 0 ?
              <MarketplaceBadge badge={{
                icon: 'bess',
                description: 'This deal has BESS - ' + listing?.totalStorageWatts + ' MW | ' + listing?.totalStorage + ' MWh'
              }} />
              : null}
          </div>
          <div className="listing-interest">
            {listing.invitedToBid ? (
              <MarketplaceBadge badge={{
                icon: 'matched',
                name: 'You are matched!',
                description: 'You have been invited to bid on this deal.'
              }}
              />
            ) :
              <>
                {listing.isInterested ? (
                  <MarketplaceBadge badge={{
                    icon: 'interested',
                    name: 'You are interested!',
                    description: interestBadgeDescription
                  }}
                  />
                ) : (
                  <MarketplaceBadge badge={{
                    icon: 'notInterested',
                    name: 'Interested in this deal?',
                    description: interestBadgeDescription
                  }}
                  />
                )}
              </>
            }
          </div>
        </div>
        <div className="listing-content">
          <div className="listing-title">
            {/* <div className="listing-icons">
              <PvIcon />
              {listing?.totalStorage && listing?.totalStorage > 0 ? <BessIcon /> : null}
            </div> */}
            <h3>
              {listing?.totalSize > 0 ? (
                <>{listing?.totalSize} MWdc</>
              ) : (
                <>
                  {listing?.totalStorageWatts + ' MW | ' + listing?.totalStorage + ' MWh'}
                </>
              )}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <div className={'project-stage ' + dealMaturityClassName}>
                {listing?.dealMaturity}
              </div>
              {listing?.totalSize === 0 && (
                <div className="project-stage bess-only">
                  BESS Only
                </div>
              )}
            </div>

          </div>
          <div className="listing-bullets">
            <div>Bid deadline</div>
            <div>{bidDeadlineDisplay}</div>
            <div>Projects</div>
            <div>{listing?.numberOfProjects}</div>
            <div>Location</div>
            <div>{stateDisplay}</div>
            <div>Project type</div>
            <div>{deploymentDisplay}</div>
            {permissions?.isAdmin ? (
              <div>Interested Co&apos;s</div>
            ) : null}

            {permissions?.isAdmin ? (
              <div>{listing?.interestedCompanies?.filter((ic: any) => ic.isInterested).length || 0}</div>
            ) : null}

          </div>

          <span className="read-more">Read more</span>
        </div>
      </div>
      {modalIsOpen &&
        (
          <Modal closeModal={() => setModalIsOpen(false)}>
            <Modal.Content>
              <div className="listing-modal">
                <h1>
                  {listing?.totalSize > 0 ? (
                    <>{listing?.totalSize} MWdc</>
                  ) : (
                    <>
                      {listing?.totalStorageWatts + ' MW | ' + listing?.totalStorage + ' MWh'}
                    </>
                  )}
                  <div className={'project-stage ' + dealMaturityClassName}>
                    {listing?.dealMaturity}
                  </div>
                  {listing?.totalSize === 0 && (
                    <div className="project-stage bess-only">
                      BESS Only
                    </div>
                  )}
                </h1>

                <h3 className="listing-subhead">
                  Bid deadline: {bidDeadlineDisplay} | Projects: {listing?.numberOfProjects} | Location: {stateDisplay} | Type: {deploymentDisplay}
                </h3>
                <div className="modal-badges">
                  {listing?.badges?.map((badge: any, idx: number) => (
                    <MarketplaceBadge key={idx} badge={badge} />
                  ))}
                  {listing?.totalStorage > 0 ?
                    <MarketplaceBadge badge={{
                      icon: 'bess',
                      description: 'This deal has BESS - ' + listing?.totalStorageWatts + ' MW | ' + listing?.totalStorage + ' MWh'
                    }} />
                    : null}
                  {listing.invitedToBid ? (
                    <MarketplaceBadge badge={{
                      icon: 'matched',
                      name: 'You are matched!',
                      description: 'You have been invited to bid on this deal.'
                    }}
                    />
                  ) :
                    <>
                      {listing.isInterested ? (
                        <MarketplaceBadge badge={{
                          icon: 'interested',
                          name: 'You are interested!',
                          description: interestBadgeDescription
                        }}
                        />
                      ) : (
                        <MarketplaceBadge badge={{
                          icon: 'notInterested',
                          name: 'Interested in this deal?',
                          description: interestBadgeDescription
                        }}
                        />
                      )}
                    </>
                  }
                </div>
                <div className="listing-actions" style={{ display: (permissions?.isDeveloper) ? 'none' : undefined }}>
                  {listing.invitedToBid ?
                    <div style={{ textAlign: 'center', margin: '0px 0 16px' }}>
                      <h3 style={{ fontSize: '16px' }}>You have been invited to bid on this deal!</h3>
                      <a href={`/${listing.projectId ? 'project' : 'portfolio'}/${listing.projectId ? listing.projectId : listing.portfolioId}`} className="ui button secondary">View {listing.projectId ? 'Project' : 'Portfolio'}</a>
                    </div>
                    :
                    <Checkbox
                      toggle
                      onChange={toggleInterest}
                      disabled={permissions?.isAdmin || permissions?.isDeveloper}
                      checked={listing.isInterested}
                      label={
                        <label>
                          <span>I am interested in {listing?.bidDeadline && new Date(listing.bidDeadline) < new Date() ? '' : 'bidding on'} this deal.</span>
                          <span>{interestBadgeDescription}</span>
                        </label>
                      }
                    />
                  }
                </div>
                <div className="description-content">
                  <span dangerouslySetInnerHTML={{ __html: listing?.description || 'No description provided' }} />
                </div>
                <div className="response-date" style={{ marginTop: '-8px', marginBottom: '8px', fontSize: '0.9em', color: '#666' }}>
                  Last updated {listing?.updatedAt ? format(new Date(listing.updatedAt), 'MMM d, yyyy h:mm a') : ''}
                </div>

                {permissions?.isAdmin ? (
                  <div className="listing-admin-container">
                    <h3>Admin Only</h3>
                    <strong>Deal Name:</strong> {listing?.dealName}<br />
                    <strong>Developer:</strong> {listing?.developerName}<br />
                    <strong>Interested Companies: </strong>
                    {listing?.interestedCompanies?.filter((ic: any) => ic.isInterested).length
                      ? listing.interestedCompanies.filter((ic: any) => ic.isInterested).map((company: any) => company.company.name).join(', ')
                      : 'None'}
                    <br /><br />
                    <a href={`/${listing?.projectId ? 'project/' + listing?.projectId : 'portfolio/' + listing?.portfolioId}/listing`} className="ui button secondary">Edit listing</a>
                  </div>
                ) : null}


              </div>
            </Modal.Content>
          </Modal>
        )
      }
    </div >
  );
};

export default MarketplaceListing;